import React, { useCallback, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { ConfirmationDialog } from 'src/components';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';

import {
  getPendingUploads,
  getRequesterAssignment,
  getReviewDocumentLink,
  getTaskIsOpen,
} from './task-review-helpers';
import { TaskDocumentCarousel } from './TaskDocumentCarousel';
import { TaskFileUpload } from './TaskFileUpload';
import { useDownloadTaskDocument } from './use-download-task-document';
import type { TaskWithComments } from './use-task-panel-state';
import { useTaskReviewActions } from './use-task-review-actions';

export type TaskDocument = TaskWithComments['workflow']['files'][number]['document'];

interface TaskDocumentsProps {
  currentDocumentId: UUID | null;
  documents: TaskDocument[];
  downloadCurrentDocument: (() => void) | undefined;
  isPreview: boolean;
  isReadOnly: boolean;
  task: TaskWithComments;
  hideHeader?: boolean;
}

/** The documents under review, with open, download and the reviewer's Word upload. */
export function TaskDocuments({
  currentDocumentId,
  documents,
  downloadCurrentDocument,
  hideHeader = false,
  isPreview,
  isReadOnly,
  task,
}: TaskDocumentsProps): JSX.Element {
  const currentUserId = useCurrentUserId();
  const { clearUpload } = useTaskReviewActions(task.id);
  const { downloadDocument } = useDownloadTaskDocument();
  const [uploadDocumentId, setUploadDocumentId] = useState<UUID | null>(null);
  const [clearingUploadId, setClearingUploadId] = useState<UUID | null>(null);
  const assignment = isPreview ? null : getRequesterAssignment(task.assignments, currentUserId);
  const canRedline =
    Boolean(assignment) &&
    getTaskIsOpen(task) &&
    Boolean(task.startedAt) &&
    assignment?.decision !== Apollo.TaskAssigneeDecision.Completed &&
    !isReadOnly;

  const handleDownload = useCallback(
    (documentId: UUID) => {
      if (documentId === currentDocumentId && downloadCurrentDocument) downloadCurrentDocument();
      else void downloadDocument(documentId);
    },
    [currentDocumentId, downloadCurrentDocument, downloadDocument]
  );
  const handleOpen = useCallback(
    (documentId: UUID) => {
      const name = documents.find(document => document.id === documentId)?.name;
      openLink(getReviewDocumentLink(documentId, task.id), {
        applyRouteToExistingTab: true,
        title: name || 'Document',
      });
    },
    [documents, task.id]
  );
  const uploadsByDocumentId = useMemo(
    () =>
      new Map(
        assignment && !isReadOnly
          ? getPendingUploads(assignment).map(upload => [
              upload.documentId,
              { fileName: upload.fileName, uploadId: upload.id, uploadedAt: upload.createdAt },
            ])
          : []
      ),
    [assignment, isReadOnly]
  );
  const confirmClearUpload = useCallback(async () => {
    if (clearingUploadId) await clearUpload(clearingUploadId);
    setClearingUploadId(null);
  }, [clearUpload, clearingUploadId]);

  return (
    <>
      <TaskDocumentCarousel
        canUpload={isPreview || canRedline}
        currentDocumentId={currentDocumentId}
        documents={documents}
        hideHeader={hideHeader}
        uploadDisabledReason={isPreview ? 'Preview only' : null}
        uploadsByDocumentId={uploadsByDocumentId}
        onClearUpload={setClearingUploadId}
        onDownload={handleDownload}
        onOpen={handleOpen}
        onUpload={setUploadDocumentId}
      />
      <ConfirmationDialog
        message="The version you uploaded will be deleted."
        open={Boolean(clearingUploadId)}
        primaryText="Remove"
        title="Remove your upload?"
        onClickPrimary={confirmClearUpload}
        onClose={() => setClearingUploadId(null)}
      />
      {uploadDocumentId && (
        <TaskFileUpload
          documentId={uploadDocumentId}
          taskId={task.id}
          onClose={() => setUploadDocumentId(null)}
        />
      )}
    </>
  );
}
